import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LicenceStatus } from '@shared/constants/licence-status';
import type {
  ClientCategory,
  LicenceCsm,
  LicenceDetail,
  LicenceResponse,
  LicenceStatsResponse,
} from '@shared/schemas';

import { clientCategoriesTransitions } from '../../_shared/domain/licence-client-categories';
import type { LicencesError } from '../domain/licence.model';
import { closedEdit, idleMutation, initialState } from './licences.state';

/**
 * Licences slice. Reducers are pure state transitions only; the actual load
 * (the `GET /licences` I/O) lives in the saga, which dispatches these `load*`
 * transitions as it progresses. This is what lets the reducers be unit-tested as
 * `(state, action) => state` with zero mocks.
 */
const slice = createSlice({
  name: 'licences',
  initialState,
  reducers: {
    loadStarted: (state) => {
      state.loadStatus = 'loading';
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<LicenceResponse[]>) => {
      state.rows = action.payload;
      state.loadStatus = 'loaded';
      state.error = null;
    },
    loadFailed: (state, action: PayloadAction<LicencesError>) => {
      state.loadStatus = 'failed';
      state.error = action.payload;
    },
    /** KPI figures landed. Supporting info, so it stands apart from `load*`. */
    statsLoadSucceeded: (state, action: PayloadAction<LicenceStatsResponse>) => {
      state.stats = action.payload;
    },
    /** KPI fetch failed; clear the band and stay quiet (no `error`, no overlay). */
    statsLoadFailed: (state) => {
      state.stats = null;
    },
    /** A row's write left for the server; the dialog's button waits on it. */
    mutationStarted: (state, action: PayloadAction<'status' | 'delete'>) => {
      state.mutation = { ...idleMutation, kind: action.payload, status: 'pending' };
    },
    /** The row now reads the status the server holds, with no reload. */
    statusChangeSucceeded: (
      state,
      action: PayloadAction<{ licenceId: string; status: LicenceStatus; revokedSessions: number }>,
    ) => {
      const { licenceId, status, revokedSessions } = action.payload;
      const row = state.rows.find((r) => r.id === licenceId);
      if (row) row.status = status;
      state.mutation = { kind: 'status', status: 'succeeded', error: null, revokedSessions };
    },
    /** The row leaves the table, with no reload. */
    deleteSucceeded: (state, action: PayloadAction<string>) => {
      state.rows = state.rows.filter((r) => r.id !== action.payload);
      state.mutation = { ...idleMutation, kind: 'delete', status: 'succeeded' };
    },
    mutationFailed: (state, action: PayloadAction<string>) => {
      state.mutation = { ...state.mutation, status: 'failed', error: action.payload };
    },
    /** The page has told the outcome; back to idle for the next action. */
    mutationCleared: (state) => {
      state.mutation = idleMutation;
    },
    // ── Edit licence, from a row's menu (dispatched by the saga) ──
    /** The dialog opened on a licence: whatever the previous opening left is gone. */
    editStarted: (state, action: PayloadAction<string>) => {
      state.edit = { ...closedEdit, licenceId: action.payload, licenceStatus: 'loading' };
    },
    editLicenceLoaded: (state, action: PayloadAction<LicenceDetail>) => {
      if (state.edit.licenceId !== action.payload.id) return;
      state.edit.licence = action.payload;
      state.edit.licenceStatus = 'loaded';
    },
    /** The form still opens from the row; only the CSM on record is unknown. */
    editLicenceLoadFailed: (state) => {
      state.edit.licenceStatus = 'failed';
    },
    csmCandidatesLoadStarted: (state) => {
      state.edit.csmCandidates.status = 'loading';
    },
    csmCandidatesLoadSucceeded: (state, action: PayloadAction<LicenceCsm[]>) => {
      state.edit.csmCandidates = { items: action.payload, status: 'loaded' };
    },
    csmCandidatesLoadFailed: (state) => {
      state.edit.csmCandidates.status = 'failed';
    },
    clientCategoriesLoadStarted: (state) => {
      clientCategoriesTransitions.loadStarted(state.edit.clientCategories);
    },
    clientCategoriesLoadSucceeded: (state, action: PayloadAction<ClientCategory[]>) => {
      clientCategoriesTransitions.loadSucceeded(state.edit.clientCategories, action.payload);
    },
    clientCategoriesLoadFailed: (state) => {
      clientCategoriesTransitions.loadFailed(state.edit.clientCategories);
    },
    clientCategoryAddStarted: (state) => {
      clientCategoriesTransitions.addStarted(state.edit.clientCategories);
    },
    clientCategoryAddSucceeded: (state, action: PayloadAction<ClientCategory>) => {
      clientCategoriesTransitions.addSucceeded(state.edit.clientCategories, action.payload);
    },
    clientCategoryAddFailed: (state, action: PayloadAction<string>) => {
      clientCategoriesTransitions.addFailed(state.edit.clientCategories, action.payload);
    },
    clientCategoryAddCleared: (state) => {
      clientCategoriesTransitions.addCleared(state.edit.clientCategories);
    },
    editSaveStarted: (state) => {
      state.edit.save = { status: 'pending', error: null };
    },
    /** The row reads what the server now holds (name, category, country) with no reload. */
    editSaveSucceeded: (state, action: PayloadAction<LicenceDetail>) => {
      const saved = action.payload;
      const row = state.rows.find((r) => r.id === saved.id);
      if (row) {
        row.name = saved.name;
        row.client_category = saved.client_category;
        row.client_category_label = saved.client_category_label;
        row.based_in = saved.based_in;
        row.updated_at = saved.updated_at;
      }
      state.edit.licence = saved;
      state.edit.save = { status: 'succeeded', error: null };
    },
    editSaveFailed: (state, action: PayloadAction<string>) => {
      state.edit.save = { status: 'failed', error: action.payload };
    },
    /** The page has told the outcome of a save; the dialog stays as it is. */
    editSaveCleared: (state) => {
      state.edit.save = { status: 'idle', error: null };
    },
    editClosed: (state) => {
      state.edit = closedEdit;
    },
    /** Reset to the pristine slice (e.g. on unmount / navigation away). */
    resetForm: () => initialState,
  },
});

export const licencesActions = slice.actions;
export const licencesReducer = slice.reducer;
